/**
 * Which company name a contact is registered under.
 *
 * `contactOrgName` is stamped onto the SDK contact row at registration and is
 * what the UI shows wherever it needs a company label — the org line on a
 * profile card, the subtitle in the calls tab and the people picker — and it is
 * the sort key the contact directory groups by.
 *
 * It used to be the literal string 'inmeet', passed for every user of every
 * tenant. That is correct for a single-tenant white-label integration, which is
 * what the field was designed for ("Company/brand name for THIS app"), and
 * wrong for inMeet: it collapses every organisation into one label, so the one
 * field that exists to tell apart people from different companies is the one
 * field that cannot.
 *
 * So the name is read from the signed-in user's own token, and 'inmeet' is kept
 * as the fallback. Today the fallback is what almost everyone gets, because no
 * claim carrying an organisation NAME is minted yet — the token has `tenantId`
 * and `orgId`, both opaque ids. That is deliberate rather than pointless: the
 * behaviour is identical to the hardcoded string until the day a name claim
 * appears, and on that day it starts working with no further client change.
 */

/** What a user is registered under when their token names no organisation. */
export const CONTACT_ORG_FALLBACK = 'inmeet'

/**
 * Claim names worth trying, best first.
 *
 * Several spellings because the claim does not exist yet and the eventual
 * producer is not this codebase: the backend mints camelCase
 * (`session.service.ts`), SecureID's own claims arrive snake_case
 * (`org_id`) and camel (`tenantId`) side by side, and `tenantSlug` is already
 * declared in `SecureIdClaims` though nothing reads it. Accepting the whole set
 * means whichever is chosen upstream simply works.
 *
 * Ids are deliberately NOT in this list. A UUID is not a company name, and
 * showing one under somebody's face is worse than showing the fallback.
 */
const ORG_NAME_CLAIMS = [
  'orgName',
  'org_name',
  'organizationName',
  'organisationName',
  'tenantName',
  'tenant_name',
  'tenantSlug',
  'tenant_slug',
] as const

/**
 * Pick the organisation name out of a token's claims.
 *
 * Pure and exported so the choice can be tested without a browser, a session or
 * a signed token — the interesting cases are all about what is missing or the
 * wrong shape, and none of them need a JWT to express.
 */
export function orgNameFromClaims(claims: unknown): string {
  return claimedOrgName(claims) ?? CONTACT_ORG_FALLBACK
}

/**
 * The organisation the token actually names, or null when it names none.
 *
 * The null matters and is why this exists separately from the fallback form.
 * Refreshing a stored name is only safe when the token genuinely carries one:
 * a user whose token has no organisation would otherwise overwrite a good
 * stored value with 'inmeet' on their next visit, turning a one-off labelling
 * bug into a recurring one that undoes itself.
 */
export function claimedOrgName(claims: unknown): string | null {
  if (!claims || typeof claims !== 'object') return null

  const record = claims as Record<string, unknown>
  for (const key of ORG_NAME_CLAIMS) {
    const value = record[key]
    // Trimmed before the emptiness test: a claim of "   " is absent as far as a
    // label is concerned, and would otherwise be stored and rendered as blank.
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

/**
 * The claims inside the app's access token, or null.
 *
 * Best-effort by design: a missing, opaque or malformed token is not an error
 * here, it just means the fallback is used. Registration must never fail
 * because a label could not be resolved.
 *
 * Reads the same storage key and the same expiry rule as the socket's own JWT
 * reader (`phoenix-socket.ts`), which is private to that module.
 */
function accessTokenClaims(): Record<string, unknown> | null {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') ||
      sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return null

    const parsed = JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }
    const session = parsed?.session
    // An expired token's claims are still readable, but registering under a
    // company name from a session that has already lapsed is not something to
    // do quietly.
    if (session?.expiresAt && session.expiresAt <= Date.now()) return null

    const payload = session?.accessToken?.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/')
    if (!payload) return null

    return JSON.parse(atob(payload)) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * The company name to register the signed-in user under.
 *
 * Never throws and never returns empty — the server treats `contactOrgName` as
 * mandatory, and a registration that failed over a display label would cost the
 * user their chat identity entirely.
 */
export function readContactOrgName(): string {
  return orgNameFromClaims(accessTokenClaims())
}

/**
 * The organisation this session's token names, or null.
 *
 * Used to decide whether an ALREADY-registered contact's stored company name
 * is stale and worth refreshing. Contacts registered before the name was
 * plumbed through carry the old hardcoded 'inmeet' for ever, because
 * `registerSelf` only runs once — at the terms gate, which never shows again.
 */
export function readClaimedOrgName(): string | null {
  return claimedOrgName(accessTokenClaims())
}
